import React, { useState, useEffect } from 'react';
import CountryFlag, { normalizeCountryCode, CountryCodeInput } from '../../../shared/components/CountryFlag';
import {
    Card,
    Table,
    Empty,
    Button,
    Space,
    Tag,
    Modal,
    Form,
    Input,
    Select,
    message,
    Tooltip,
    InputNumber,
    Badge,
    Divider,
    Transfer
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    ReloadOutlined,
    BranchesOutlined,
    CloudServerOutlined,
    GlobalOutlined
} from '@ant-design/icons';
import cdnService from '../../services/cdnService';

const { Option } = Select;
const { TextArea } = Input;

const CdnLines = () => {
    const [lines, setLines] = useState([]);
    const [groups, setGroups] = useState([]);
    const [nodes, setNodes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [nodeModalVisible, setNodeModalVisible] = useState(false);
    const [editingLine, setEditingLine] = useState(null);
    const [selectedLine, setSelectedLine] = useState(null);
    const [selectedGroupId, setSelectedGroupId] = useState(null);
    const [targetKeys, setTargetKeys] = useState([]);
    const [form] = Form.useForm();

    useEffect(() => {
        loadGroups();
        loadNodes();
        if (selectedGroupId) {
            loadLines(selectedGroupId);
        }
    }, [selectedGroupId]);

    const loadGroups = async () => {
        try {
            const response = await cdnService.getCdnGroups(null, 'active');
            if (response.success) {
                const groupData = response.data || [];
                setGroups(groupData);
                if (groupData.length > 0 && !selectedGroupId) {
                    setSelectedGroupId(groupData[0].id);
                }
            }
        } catch (error) {
            message.error('加载分组失败: ' + error.message);
        }
    };

    const loadLines = async (groupId) => {
        setLoading(true);
        try {
            const response = await cdnService.getCdnLines(groupId);
            if (response.success) {
                setLines(response.data || []);
            } else {
                message.error(response.error || '加载线路失败');
            }
        } catch (error) {
            message.error('加载线路失败: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const loadNodes = async () => {
        try {
            const response = await cdnService.getNodes();
            if (response.success) {
                setNodes(response.data || []);
            }
        } catch (error) {
            console.error('加载节点失败:', error);
        }
    };

    const handleAdd = () => {
        if (!selectedGroupId) {
            message.warning('请先选择一个CDN分组');
            return;
        }
        setEditingLine(null);
        form.resetFields();
        form.setFieldsValue({
            group_id: selectedGroupId,
            line_type: 'default',
            priority: 100,
            weight: 100,
            status: 'active'
        });
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingLine(record);
        form.setFieldsValue({
            group_id: record.group_id,
            name: record.name,
            description: record.description,
            line_type: record.line_type,
            geo_continent: record.geo_continent,
            geo_country: record.geo_country,
            geo_province: record.geo_province,
            geo_city: record.geo_city,
            isp: record.isp,
            priority: record.priority,
            weight: record.weight,
            status: record.status
        });
        setModalVisible(true);
    };

    const handleDelete = (id, name) => {
        Modal.confirm({
            title: '确认删除',
            content: `确定要删除线路 "${name}" 吗？`,
            okText: '确定',
            cancelText: '取消',
            okType: 'danger',
            onOk: async () => {
                try {
                    const response = await cdnService.deleteCdnLine(id);
                    if (response.success) {
                        message.success('删除成功');
                        loadLines(selectedGroupId);
                    } else {
                        message.error(response.error || '删除失败');
                    }
                } catch (error) {
                    message.error('删除失败: ' + error.message);
                }
            }
        });
    };

    const handleManageNodes = (record) => {
        setSelectedLine(record);
        setTargetKeys(record.nodes?.map(n => n.id) || []);
        setNodeModalVisible(true);
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            const response = editingLine
                ? await cdnService.updateCdnLine({ ...values, id: editingLine.id })
                : await cdnService.createCdnLine(values);

            if (response.success) {
                message.success(editingLine ? '更新成功' : '创建成功');
                setModalVisible(false);
                loadLines(selectedGroupId);
            } else {
                message.error(response.error || '操作失败');
            }
        } catch (error) {
            if (error.errorFields) {
                message.error('请填写所有必填项');
            } else {
                message.error('操作失败: ' + error.message);
            }
        }
    };

    const handleSaveNodes = async () => {
        try {
            // Get current node IDs
            const currentNodeIds = selectedLine.nodes?.map(n => n.id) || [];

            // Find nodes to add and remove
            const nodesToAdd = targetKeys.filter(id => !currentNodeIds.includes(id));
            const nodesToRemove = currentNodeIds.filter(id => !targetKeys.includes(id));

            // Remove nodes
            for (const nodeId of nodesToRemove) {
                await cdnService.removeNodeFromLine(selectedLine.id, nodeId);
            }

            // Add nodes
            for (const nodeId of nodesToAdd) {
                await cdnService.addNodeToLine(selectedLine.id, nodeId);
            }

            message.success('节点配置已更新');
            setNodeModalVisible(false);
            loadLines(selectedGroupId);
        } catch (error) {
            message.error('保存失败: ' + error.message);
        }
    };

    const getLineTypeTag = (type) => {
        const colors = {
            default: 'blue',
            telecom: 'green',
            unicom: 'orange',
            mobile: 'purple',
            abroad: 'cyan'
        };
        const names = {
            default: '默认',
            telecom: '电信',
            unicom: '联通',
            mobile: '移动',
            abroad: '海外'
        };
        return <Tag color={colors[type]}>{names[type] || type}</Tag>;
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 60,
        },
        {
            title: '线路名称',
            dataIndex: 'name',
            key: 'name',
            render: (text) => (
                <Space>
                    <BranchesOutlined />
                    <strong>{text}</strong>
                </Space>
            )
        },
        {
            title: '类型',
            dataIndex: 'line_type',
            key: 'line_type',
            width: 100,
            render: (type) => getLineTypeTag(type)
        },
        {
            title: '地理位置',
            key: 'geo',
            width: 200,
            render: (_, record) => {
                const geoInfo = [];
                if (record.geo_continent) geoInfo.push(record.geo_continent);
                if (record.geo_country) geoInfo.push(record.geo_country);
                if (record.geo_province) geoInfo.push(record.geo_province);
                if (record.geo_city) geoInfo.push(record.geo_city);

                return geoInfo.length > 0 ? (
                    <Space size={4}>
                        {record.geo_country && normalizeCountryCode(record.geo_country) ? <CountryFlag code={record.geo_country} style={{ marginInlineEnd: 0 }} /> : <GlobalOutlined />}
                        {geoInfo.join(' / ')}
                    </Space>
                ) : '-';
            }
        },
        {
            title: 'ISP',
            dataIndex: 'isp',
            key: 'isp',
            width: 80,
            render: (isp) => isp || '-'
        },
        {
            title: '优先级',
            dataIndex: 'priority',
            key: 'priority',
            width: 80,
            sorter: (a, b) => a.priority - b.priority,
        },
        {
            title: '权重',
            dataIndex: 'weight',
            key: 'weight',
            width: 80,
        },
        {
            title: '节点数',
            dataIndex: 'node_count',
            key: 'node_count',
            width: 80,
            render: (count) => (
                <Badge count={count} showZero color="#52c41a" />
            )
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            width: 80,
            render: (status) => (
                <Tag color={status === 'active' ? 'green' : 'red'}>
                    {status === 'active' ? '启用' : '禁用'}
                </Tag>
            )
        },
        {
            title: '操作',
            key: 'action',
            width: 240,
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="管理节点">
                        <Button
                            type="link"
                            icon={<CloudServerOutlined />}
                            onClick={() => handleManageNodes(record)}
                        >
                            节点
                        </Button>
                    </Tooltip>
                    <Tooltip title="编辑">
                        <Button
                            type="link"
                            icon={<EditOutlined />}
                            onClick={() => handleEdit(record)}
                        >
                            编辑
                        </Button>
                    </Tooltip>
                    <Tooltip title="删除">
                        <Button
                            type="link"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => handleDelete(record.id, record.name)}
                        >
                            删除
                        </Button>
                    </Tooltip>
                </Space>
            )
        }
    ];

    return (
        <div>
            <Card
                title={
                    <Space>
                        <BranchesOutlined />
                        CDN线路管理
                    </Space>
                }
                extra={
                    <Space>
                        <Select
                            style={{ width: 200 }}
                            placeholder="选择分组"
                            value={selectedGroupId}
                            onChange={setSelectedGroupId}
                        >
                            {groups.map(group => (
                                <Option key={group.id} value={group.id}>
                                    {group.name}
                                </Option>
                            ))}
                        </Select>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={() => loadLines(selectedGroupId)}
                        >
                            刷新
                        </Button>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={handleAdd}
                            disabled={!selectedGroupId}
                        >
                            新建线路
                        </Button>
                    </Space>
                }
            >
                <Table
                    columns={columns}
                    dataSource={lines}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showTotal: (total) => `共 ${total} 条`
                    }}
                    scroll={{ x: 'max-content' }}
                    locale={{ emptyText: <Empty description="暂无线路，点击右上角新建线路" /> }}
                />
            </Card>

            <Modal
                title={editingLine ? '编辑线路' : '新建线路'}
                open={modalVisible}
                onOk={handleSubmit}
                onCancel={() => setModalVisible(false)}
                width={700}
                okText="确定"
                cancelText="取消"
            >
                <Form
                    form={form}
                    layout="vertical"
                >
                    <Form.Item name="group_id" hidden>
                        <Input />
                    </Form.Item>

                    <Form.Item
                        name="name"
                        label="线路名称"
                        rules={[{ required: true, message: '请输入线路名称' }]}
                    >
                        <Input placeholder="例如：电信线路" />
                    </Form.Item>

                    <Form.Item name="description" label="描述">
                        <TextArea rows={2} placeholder="线路描述（可选）" />
                    </Form.Item>

                    <Form.Item
                        name="line_type"
                        label="线路类型"
                        rules={[{ required: true, message: '请选择线路类型' }]}
                    >
                        <Select>
                            <Option value="default">默认线路</Option>
                            <Option value="telecom">电信</Option>
                            <Option value="unicom">联通</Option>
                            <Option value="mobile">移动</Option>
                            <Option value="abroad">海外</Option>
                        </Select>
                    </Form.Item>

                    <Divider>地理位置匹配（可选）</Divider>

                    <Form.Item name="geo_continent" label="大洲">
                        <Input placeholder="例如：Asia" />
                    </Form.Item>

                    <Form.Item name="geo_country" label="国家代码">
                        <CountryCodeInput placeholder="例如：CN" />
                    </Form.Item>

                    <Form.Item name="geo_province" label="省份">
                        <Input placeholder="例如：广东" />
                    </Form.Item>

                    <Form.Item name="geo_city" label="城市">
                        <Input placeholder="例如：深圳" />
                    </Form.Item>

                    <Form.Item name="isp" label="ISP运营商">
                        <Input placeholder="例如：电信" />
                    </Form.Item>

                    <Divider>优先级与权重</Divider>

                    <Space style={{ width: '100%' }} size="large">
                        <Form.Item
                            name="priority"
                            label="优先级"
                            rules={[{ required: true, message: '请输入优先级' }]}
                            extra="数值越小越优先"
                            style={{ flex: 1 }}
                        >
                            <InputNumber
                                min={1}
                                max={1000}
                                style={{ width: '100%' }}
                            />
                        </Form.Item>

                        <Form.Item
                            name="weight"
                            label="权重"
                            rules={[{ required: true, message: '请输入权重' }]}
                            extra="负载均衡权重"
                            style={{ flex: 1 }}
                        >
                            <InputNumber
                                min={1}
                                max={1000}
                                style={{ width: '100%' }}
                            />
                        </Form.Item>

                        <Form.Item
                            name="status"
                            label="状态"
                            rules={[{ required: true, message: '请选择状态' }]}
                            style={{ flex: 1 }}
                        >
                            <Select>
                                <Option value="active">启用</Option>
                                <Option value="inactive">禁用</Option>
                            </Select>
                        </Form.Item>
                    </Space>
                </Form>
            </Modal>

            <Modal
                title={`管理线路节点 - ${selectedLine?.name}`}
                open={nodeModalVisible}
                onOk={handleSaveNodes}
                onCancel={() => setNodeModalVisible(false)}
                width={700}
                okText="保存"
                cancelText="取消"
            >
                <Transfer
                    dataSource={nodes.map(node => ({
                        key: node.id,
                        title: `${node.name} (${node.hostname})`,
                        description: `${node.region || ''} ${node.ip || ''}`,
                        disabled: node.status !== 'active'
                    }))}
                    titles={['可用节点', '已选节点']}
                    targetKeys={targetKeys}
                    onChange={setTargetKeys}
                    render={item => item.title}
                    listStyle={{
                        width: 300,
                        height: 400,
                    }}
                    showSearch
                    filterOption={(inputValue, item) =>
                        item.title.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1
                    }
                />
            </Modal>
        </div>
    );
};

export default CdnLines;
